import { z } from "zod";

// defined as enum on backend, but object here to make changes easier
// and backwards compatible
const projectCompletedActionsSchema = z
  .object({
    audioCreated: z.number(),
    audioRangesSaved: z.number(),
    auditionsCreated: z.number(),
    videosCreated: z.number(),
  })
  .partial();

export const projectListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  videoCount: z.number(),
  createdAt: z.string(),
  thumbnailUrl: z.string().nullable(),
  latestAudioId: z.string().nullable(),
  completedActions: projectCompletedActionsSchema.default({}),
});
export type ProjectListItemType = z.infer<typeof projectListItemSchema>;

export const listProjectsResponseSchema = z.object({
  projects: z.object({
    page: z.array(projectListItemSchema),
    pageInfo: z.object({
      endCursor: z.string().nullable(),
      hasNextPage: z.boolean(),
    }),
  }),
});

const projectDetailsAudioSchema = z.object({
  id: z.string(),
  url: z.string().url(),
  type: z.enum(["uploaded", "tts"]),
});
export type ProjectDetailsAudioType = z.infer<typeof projectDetailsAudioSchema>;

const projectDetailsActorSchema = z.object({
  id: z.string(),
  thumbnailUrl: z.string().url(),
});
export type ProjectDetailsActorType = z.infer<typeof projectDetailsActorSchema>;

export const getProjectDetailsResponseSchema = z.object({
  project: z.object({
    id: z.string(),
    title: z.string(),
    orgId: z.string(),
    audios: z.array(projectDetailsAudioSchema),
    actors: z.array(projectDetailsActorSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedActions: projectCompletedActionsSchema.default({}),
    videoCount: z.number(),
  }),
});
export type ProjectDetailsType = z.infer<typeof getProjectDetailsResponseSchema>["project"];

const wordTimestampSchema = z.object({
  text: z.string(),
  startMs: z.number(),
  endMs: z.number(),
});

const clipRangeSchema = z.object({
  startMs: z.number(),
  endMs: z.number(),
});

const errorSchema = z.object({
  code: z.string(),
  message: z.string().nullable(),
});
export type VideoErrorType = z.infer<typeof errorSchema>;

const pollAudioClipRangesBaseSchema = z.object({});

export const pollAudioClipRangesResponseSchema = z.discriminatedUnion("state", [
  pollAudioClipRangesBaseSchema.extend({
    state: z.literal("PROCESSING"),
    progress: z.number(),
  }),
  pollAudioClipRangesBaseSchema.extend({
    state: z.literal("COMPLETE"),
    wordTimestamps: z.array(wordTimestampSchema),
    defaultClipRanges: z.array(clipRangeSchema),
    audioDurationMs: z.number(),
    audioUrl: z.string(),
    audioId: z.string(),
  }),
  pollAudioClipRangesBaseSchema.extend({
    state: z.literal("FAILED"),
    error: errorSchema,
  }),
  pollAudioClipRangesBaseSchema.extend({
    state: z.literal("CANCELLED"),
  }),
]);

export const auditionFromAudioVideoItemSchema = z.discriminatedUnion("state", [
  z.object({
    id: z.string(),
    state: z.literal("QUEUED"),
  }),
  z.object({
    id: z.string(),
    state: z.literal("PROCESSING"),
    progress: z.number(),
    estimatedRemainingTimeMs: z.number().nullable(),
    previewThumbnailUrl: z.string().nullish(),
  }),
  z.object({
    id: z.string(),
    state: z.literal("COMPLETE"),
    videoUrl: z.string(),
    thumbnailUrl: z.string(),
    actorId: z.string(),
  }),
  z.object({
    id: z.string(),
    state: z.literal("FAILED"),
    error: errorSchema,
  }),
  z.object({
    id: z.string(),
    state: z.literal("CANCELLED"),
  }),
]);
export type AuditionFromAudioVideoItemType = z.infer<typeof auditionFromAudioVideoItemSchema>;

export const pollAuditionResponseSchema = z.object({
  video: auditionFromAudioVideoItemSchema,
});

export const pollAuditionRequestSchema = z.object({
  auditionOperationId: z.string(),
  audioId: z.string(),
  projectId: z.string(),
});
export type PollAuditionRequestType = z.infer<typeof pollAuditionRequestSchema>;

export const auditionFromAudioItemSchema = z.object({
  id: z.string(),
  actor: z.object({}).optional(), // TODO: Add actor schema when needed
  video: auditionFromAudioVideoItemSchema,
});
export type AuditionFromAudioItemType = z.infer<typeof auditionFromAudioItemSchema>;

export const listAuditionsForAudioResponseSchema = z.object({
  auditions: z.array(auditionFromAudioItemSchema),
});

export const generateVideoFromAuditionResponseSchema = z.object({
  operationId: z.string(),
});

const listProjectVideoItemBaseSchema = z.object({
  id: z.string(),
  thumbnailUrl: z.string().url().nullish(),
  createdAt: z.string(),
});

export const listProjectVideoItemSchema = z.discriminatedUnion("state", [
  listProjectVideoItemBaseSchema.extend({
    state: z.literal("QUEUED"),
  }),
  listProjectVideoItemBaseSchema.extend({
    state: z.literal("PROCESSING"),
    progress: z.number(),
    estimatedRemainingTimeMs: z.number().nullish(),
  }),
  listProjectVideoItemBaseSchema.extend({
    state: z.literal("COMPLETE"),
  }),
  listProjectVideoItemBaseSchema.extend({
    state: z.literal("FAILED"),
    error: errorSchema,
  }),
  listProjectVideoItemBaseSchema.extend({
    state: z.literal("CANCELLED"),
  }),
]);
export type ListProjectVideoItemType = z.infer<typeof listProjectVideoItemSchema>;

export const listProjectVideosResponseSchema = z.object({
  videos: z.object({
    page: z.array(listProjectVideoItemSchema),
    pageInfo: z.object({
      hasPrevPage: z.boolean().optional(),
      hasNextPage: z.boolean().optional(),
      startCursor: z.string().nullish(),
      endCursor: z.string().nullish(),
    }),
  }),
});
export type ListProjectVideosResponseType = z.infer<typeof listProjectVideosResponseSchema>;

export const videoVariantSchema = z.discriminatedUnion("state", [
  z.object({
    clipId: z.string(),
    state: z.literal("COMPLETE"),
    videoUrl: z.string().url(),
    thumbnailUrl: z.string(),
    completedOn: z.string().optional(),
  }),
  z.object({
    clipId: z.string(),
    state: z.enum(["PROCESSING", "QUEUED", "FAILED"]),
  }),
]);

export const videoClipSchema = z.object({
  index: z.number(),
  startMs: z.number(),
  endMs: z.number(),
  transcriptText: z.string(),
  selectedClipId: z.string(),
  variants: z.array(videoVariantSchema).transform((variants) => {
    const completed = variants
      .filter((variant) => variant.state === "COMPLETE")
      .sort((a, b) => {
        if (a.completedOn && b.completedOn) {
          return new Date(b.completedOn).getTime() - new Date(a.completedOn).getTime();
        } else {
          return 0;
        }
      });
    const pending = variants.filter(
      (variant) => variant.state === "PROCESSING" || variant.state === "QUEUED"
    );
    const rest = variants.filter(
      (variant) =>
        variant.state !== "COMPLETE" && variant.state !== "PROCESSING" && variant.state !== "QUEUED"
    );
    return [...pending, ...completed, ...rest];
  }),
});

export const getVideoDetailsResponseSchema = z.object({
  id: z.string(),
  clips: z.array(videoClipSchema),
  audioId: z.string(),
});

export type VideoDetailsType = z.infer<typeof getVideoDetailsResponseSchema>;
export type VideoDetailsClipItemType = z.infer<typeof videoClipSchema>;
export type VideoDetailsClipItemVariantType = z.infer<typeof videoVariantSchema>;

export const videoGenerationStatusRequestSchema = z.object({
  videoOperationId: z.string(),
  projectId: z.string(),
});
export type VideoGenerationStatusRequestType = z.infer<typeof videoGenerationStatusRequestSchema>;

export const videoGenerationStatusResponseSchema = z.discriminatedUnion("state", [
  z.object({
    id: z.string(),
    state: z.literal("QUEUED"),
    estimatedRemainingTimeMs: z.number().nullish(),
    thumbnailUrl: z.string().nullish(),
    createdAt: z.string(),
  }),
  z.object({
    id: z.string(),
    state: z.literal("PROCESSING"),
    progress: z.number(),
    estimatedRemainingTimeMs: z.number().nullish(),
    thumbnailUrl: z.string().nullish(),
    createdAt: z.string(),
  }),
  z.object({
    id: z.string(),
    state: z.literal("COMPLETE"),
    thumbnailUrl: z.string().nullish(),
    actorId: z.string(),
    createdAt: z.string(),
    completedAt: z.string(),
  }),
  z.object({
    id: z.string(),
    state: z.literal("FAILED"),
    error: errorSchema,
    createdAt: z.string(),
  }),
  z.object({
    id: z.string(),
    state: z.literal("CANCELLED"),
    createdAt: z.string(),
  }),
]);
export type VideoGenerationStatusType = z.infer<typeof videoGenerationStatusResponseSchema>;

export const clipRegenerationPollingResponseSchema = z.discriminatedUnion("state", [
  z.object({
    videoId: z.string(),
    clipId: z.string(),
    clipIndex: z.number(),
    state: z.literal("COMPLETE"),
    progress: z.number().optional(),
    clipVideoUrl: z.string(),
    thumbnailUrl: z.string(),
    completedAt: z.string(),
  }),
  z.object({
    videoId: z.string(),
    clipId: z.string(),
    clipIndex: z.number(),
    state: z.enum(["PROCESSING", "FAILED", "QUEUED"]),
    progress: z.number().optional(),
  }),
]);
